const WIDEST_TILE = 16 / 9;
const TALLEST_PORTRAIT_TILE = 3 / 4;
const COMPACT_STAGE_BELOW_WIDTH = 600;

const half = value => value / 2;
function span(count, size, gap) {
  const tiles = count * size;
  const gaps = (count - 1) * gap;

  return tiles + gaps;
}

export function tileAspectRange(width, height) {
  const isPortraitStage = height > width;

  return { min: isPortraitStage ? TALLEST_PORTRAIT_TILE : WIDEST_TILE, max: WIDEST_TILE };
}

export function stageSpacing(width) {
  return width < COMPACT_STAGE_BELOW_WIDTH ? { gap: 6, padding: 6 } : { gap: 10, padding: 12 };
}

function tileSizeForCell(cellWidth, cellHeight, aspect) {
  const width = Math.min(cellWidth, cellHeight * aspect.max);
  const height = Math.min(cellHeight, width / aspect.min);

  return { width, height };
}

function candidate(count, columns, stage) {
  const rows = Math.ceil(count / columns);
  const frame = 2 * stage.padding;
  const usableWidth = stage.width - frame - span(columns, 0, stage.gap);
  const usableHeight = stage.height - frame - span(rows, 0, stage.gap);
  if (usableWidth <= 0 || usableHeight <= 0) {
    return null;
  }
  const tile = tileSizeForCell(usableWidth / columns, usableHeight / rows, stage.aspect);

  return { columns, rows, tile, area: tile.width * tile.height };
}

function bestCandidate(count, stage) {
  let best = null;
  for (let columns = 1; columns <= count; columns = columns + 1) {
    const next = candidate(count, columns, stage);
    if (next && (!best || next.area > best.area + 0.5)) {
      best = next;
    }
  }

  return best;
}

const HELD_COLUMNS_MIN_AREA_RATIO = 0.6;

const fitsColumns = (columns, count) => columns !== null && columns >= 1 && columns <= count;

function preferredCandidate(count, preferredColumns, stage, best) {
  if (!fitsColumns(preferredColumns, count) || preferredColumns === best.columns) {
    return null;
  }
  const held = candidate(count, preferredColumns, stage);
  const keepsTilesLarge = held && held.area >= best.area * HELD_COLUMNS_MIN_AREA_RATIO && held.tile.width >= MIN_LEGIBLE_TILE_WIDTH;

  return keepsTilesLarge ? held : null;
}

function chooseCandidate(count, stage, lockedColumns, preferredColumns) {
  const locked = fitsColumns(lockedColumns, count) ? candidate(count, lockedColumns, stage) : null;
  if (locked) {
    return locked;
  }
  const best = bestCandidate(count, stage);

  return best && (preferredCandidate(count, preferredColumns, stage, best) || best);
}

export function gridLayout({ count, width, height, gap, padding, columns: lockedColumns = null, preferredColumns = null }) {
  const empty = { columns: 0, rows: 0, boxes: [] };
  if (count < 1 || width <= 0 || height <= 0) {
    return empty;
  }
  const stage = { width, height, gap, padding, aspect: tileAspectRange(width, height) };
  const best = chooseCandidate(count, stage, lockedColumns, preferredColumns);
  if (!best) {
    return empty;
  }
  const { columns, rows, tile } = best;
  const top = half(height - span(rows, tile.height, gap));
  const boxes = [];
  for (let index = 0; index < count; index = index + 1) {
    const row = Math.floor(index / columns);
    const column = index % columns;
    const tilesBeforeRow = row * columns;
    const inThisRow = row === rows - 1 ? count - tilesBeforeRow : columns;
    const rowLeft = half(width - span(inThisRow, tile.width, gap));
    const columnOffset = column * (tile.width + gap);
    const rowOffset = row * (tile.height + gap);
    boxes.push({
      left: rowLeft + columnOffset,
      top: top + rowOffset,
      width: tile.width,
      height: tile.height,
    });
  }

  return { columns, rows, boxes };
}

export function enteringBox(box, scale) {
  const width = box.width * scale;
  const height = box.height * scale;

  return {
    left: box.left + half(box.width - width),
    top: box.top + half(box.height - height),
    width,
    height,
  };
}

const STAGE_LAYOUT_CHOICES = ["auto", "tiled", "spotlight", "sidebar"];
const AUTO_TILES_FROM_COUNT = 3;

const AUTO_SIDEBAR_COUNT = AUTO_TILES_FROM_COUNT - 1;
export const AUTO_LAYOUT_SUMMARY = `Sidebar while someone presents or with ${AUTO_SIDEBAR_COUNT} people,`
  + ` Tiled with ${AUTO_TILES_FROM_COUNT} or more`;

export function resolveStageLayout(layout, { tileCount, hasScreenShare, isFocused = false }) {
  if (!STAGE_LAYOUT_CHOICES.includes(layout)) {
    throw new Error(`Stage layout ${JSON.stringify(layout)} is not one of ${STAGE_LAYOUT_CHOICES.join(", ")}`);
  }
  if (isFocused) {
    return layout === "spotlight" ? "spotlight" : "sidebar";
  }
  if (layout !== "auto") {
    return layout;
  }
  if (hasScreenShare || tileCount < AUTO_TILES_FROM_COUNT) {
    return "sidebar";
  }

  return "tiled";
}

const THUMB_ASPECT = 16 / 9;
const SIDEBAR_STRIP_RATIO = 0.2;
const SIDEBAR_MIN_STRIP_WIDTH = 160;
const SIDEBAR_MAX_STRIP_WIDTH = 300;
const PORTRAIT_STRIP_RATIO = 0.18;
const PORTRAIT_MIN_THUMB_HEIGHT = 72;
const PORTRAIT_MAX_THUMB_HEIGHT = 144;

const clamp = (value, low, high) => Math.min(high, Math.max(low, value));
const twice = value => value * 2;
const insetBy = (size, padding) => size - twice(padding);
const slotOffset = (index, size, gap) => index * (size + gap);

function stripSlots(count, hiddenCount, slotsThatFit) {
  const needsOverflow = hiddenCount > 0;
  const slotsNeeded = count + (needsOverflow ? 1 : 0);
  if (slotsNeeded <= slotsThatFit) {
    return { thumbCount: count, hasOverflow: needsOverflow };
  }

  return { thumbCount: Math.max(0, slotsThatFit - 1), hasOverflow: true };
}

function fitWidescreen(area) {
  const width = Math.min(area.width, area.height * WIDEST_TILE);
  const height = width / WIDEST_TILE;

  return { left: area.left + half(area.width - width), top: area.top + half(area.height - height), width, height };
}

export function spotlightLayout({ width, height, padding }) {
  const area = { left: padding, top: padding, width: insetBy(width, padding), height: insetBy(height, padding) };

  return height > width ? area : fitWidescreen(area);
}

function landscapeSidebar({ count, hiddenCount, width, height, gap, padding }) {
  const innerWidth = insetBy(width, padding);
  const innerHeight = insetBy(height, padding);
  const stripWidth = clamp(width * SIDEBAR_STRIP_RATIO, SIDEBAR_MIN_STRIP_WIDTH, SIDEBAR_MAX_STRIP_WIDTH);
  const primary = fitWidescreen({ left: padding, top: padding, width: innerWidth - stripWidth - gap, height: innerHeight });
  const naturalThumbHeight = stripWidth / THUMB_ASPECT;
  const slotsThatFit = Math.max(1, Math.floor((primary.height + gap) / (naturalThumbHeight + gap)));
  const { thumbCount, hasOverflow } = stripSlots(count, hiddenCount, slotsThatFit);
  const slots = thumbCount + (hasOverflow ? 1 : 0);
  const isFull = slots === slotsThatFit;
  const thumbHeight = isFull ? (primary.height - span(slots, 0, gap)) / slots : naturalThumbHeight;
  const stripLeft = width - padding - stripWidth;
  const stripTop = isFull ? primary.top : primary.top + half(primary.height - span(slots, thumbHeight, gap));
  const slotBox = index => ({ left: stripLeft, top: stripTop + slotOffset(index, thumbHeight, gap), width: stripWidth, height: thumbHeight });

  return {
    primary,
    thumbs: Array.from({ length: thumbCount }, (_, index) => slotBox(index)),
    overflow: hasOverflow ? slotBox(thumbCount) : null,
  };
}

function portraitSidebar({ count, hiddenCount, width, height, gap, padding }) {
  const innerWidth = insetBy(width, padding);
  const innerHeight = insetBy(height, padding);
  const thumbHeight = clamp(height * PORTRAIT_STRIP_RATIO, PORTRAIT_MIN_THUMB_HEIGHT, PORTRAIT_MAX_THUMB_HEIGHT);
  const naturalThumbWidth = thumbHeight * THUMB_ASPECT;
  const slotsThatFit = Math.max(1, Math.floor((innerWidth + gap) / (naturalThumbWidth + gap)));
  const { thumbCount, hasOverflow } = stripSlots(count, hiddenCount, slotsThatFit);
  const slots = thumbCount + (hasOverflow ? 1 : 0);
  const isFull = slots === slotsThatFit;
  const thumbWidth = isFull ? (innerWidth - span(slots, 0, gap)) / slots : naturalThumbWidth;
  const stripTop = height - padding - thumbHeight;
  const stripLeft = isFull ? padding : padding + half(innerWidth - span(slots, thumbWidth, gap));
  const slotBox = index => ({ left: stripLeft + slotOffset(index, thumbWidth, gap), top: stripTop, width: thumbWidth, height: thumbHeight });

  return {
    primary: { left: padding, top: padding, width: innerWidth, height: innerHeight - thumbHeight - gap },
    thumbs: Array.from({ length: thumbCount }, (_, index) => slotBox(index)),
    overflow: hasOverflow ? slotBox(thumbCount) : null,
  };
}

export function sidebarLayout({ count, hiddenCount = 0, width, height, gap, padding }) {
  if (width <= 0 || height <= 0) {
    return { primary: null, thumbs: [], overflow: null };
  }
  if (count < 1 && hiddenCount < 1) {
    return {
      primary: { left: padding, top: padding, width: insetBy(width, padding), height: insetBy(height, padding) },
      thumbs: [],
      overflow: null,
    };
  }
  const stage = { count, hiddenCount, width, height, gap, padding };

  return height > width ? portraitSidebar(stage) : landscapeSidebar(stage);
}

const MIN_LEGIBLE_TILE_WIDTH = 160;

export function effectiveTileCap({ maxTiles, width, height, gap, padding }) {
  for (let cap = maxTiles; cap > 1; cap = cap - 1) {
    const firstBox = gridLayout({ count: cap, width, height, gap, padding }).boxes[0];
    if (firstBox && firstBox.width >= MIN_LEGIBLE_TILE_WIDTH) {
      return cap;
    }
  }

  return 1;
}

export function capStageTiles(tiles, cap) {
  if (tiles.length <= cap) {
    return { visible: tiles, overflow: [] };
  }
  const visibleCount = Math.max(0, cap - 1);

  return { visible: tiles.slice(0, visibleCount), overflow: tiles.slice(visibleCount) };
}

function tileHasVideoTrack(tile) {
  return Boolean(tile?.videoTrack) || (tile?.stream?.getVideoTracks?.().length || 0) > 0;
}

export function tileShowsVideo(tile, { isCameraEnabled }) {
  if (tile?.isScreenShare) {
    return true;
  }
  if (tile?.isLocal) {
    return isCameraEnabled && tileHasVideoTrack(tile);
  }

  return tileHasVideoTrack(tile) && !tile?.isVideoMuted;
}

const ONE_TO_ONE_TILE_COUNT = 2;

export function filterTilesWithoutVideo(tiles, { isCameraEnabled, keepIds = [] }) {
  if (tiles.length <= ONE_TO_ONE_TILE_COUNT) {
    return { shown: tiles, hidden: [] };
  }
  const shown = [];
  const hidden = [];
  tiles.forEach(tile => {
    const alwaysShown = tile.isLocal || tile.isScreenShare || keepIds.includes(tile.id);
    (alwaysShown || tileShowsVideo(tile, { isCameraEnabled }) ? shown : hidden).push(tile);
  });

  return { shown, hidden };
}
